import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TrendingUp, Lock, Delete } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const [passcode, setPasscode] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    // Only auto-forward if router pushed the user here (e.g. from ProtectedRoute after a session expiry).
    // If the user typed /login in the address bar or reloaded, always show the passcode prompt.
    const cameFromInternalNav = window.history.state?.usr?.forwarded === true;
    if (!loading && user && cameFromInternalNav) {
      if (user?.role === 'tv_kiosk') navigate('/tv');
      else navigate('/');
    }
  }, [user, loading, navigate]);

  const doLogin = useCallback(async (code, attempt = 0) => {
    if (!code || code.length < 4 || isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      const response = await api.post("/auth/login", {
        passcode: code,
        remember_me: true
      });
      const data = response.data;
      localStorage.setItem('session_token', data.session_token);
      setUser(data.user);
      const role = data.user?.role;
      if (role === 'tv_kiosk') {
        toast.success('Falcon View unlocked');
        navigate('/tv');
      } else {
        toast.success(`Welcome back, ${data.user.name}!`);
        navigate('/');
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const isNetworkErr = !error.response;                  // server unreachable / CORS / timeout
      const isServerErr = status && status >= 500;           // 502/503/504/520 etc — server sleeping
      const isRealInvalidPin = status === 401 || (status === 422 && !!detail);

      if (isNetworkErr || isServerErr) {
        // Auto-retry once with backoff — production sometimes takes a few seconds to wake
        if (attempt < 2) {
          toast.info(`Server warming up… retrying (${attempt + 1}/2)`, { duration: 2500 });
          setIsLoggingIn(false);
          await new Promise(r => setTimeout(r, 2500 * (attempt + 1)));
          return doLogin(code, attempt + 1);
        }
        toast.error(
          isNetworkErr
            ? "Can't reach the server. Check your connection or try again in a minute — the backend may be waking up."
            : `Server error (${status}). The backend appears to be down — keep your PIN, just hit Unlock again in a minute.`,
          { duration: 6000 }
        );
        // Do NOT clear the PIN field on infra errors so user can just retry
        return;
      }

      if (isRealInvalidPin) {
        toast.error("Invalid passcode");
        setPasscode("");
      } else {
        toast.error(detail || `Login failed (${status || "unknown"}). Try again.`);
        setPasscode("");
      }
    } finally {
      setIsLoggingIn(false);
    }
  }, [isLoggingIn, navigate, setUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-profit border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
      <Card className="w-full max-w-sm card-dark border-border">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-14 h-14 bg-profit/20 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-profit" />
          </div>
          <CardTitle className="font-heading text-2xl sm:text-3xl tracking-tight">
            SP WEALTH TRACKER
          </CardTitle>
          <CardDescription className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
            <Lock className="w-4 h-4" />
            Enter passcode
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 pt-2">
          {/* Passcode input field - works on all devices */}
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoFocus
            autoComplete="off"
            value={passcode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setPasscode(val);
            }}
            placeholder="Enter PIN"
            className="w-full h-16 text-center text-3xl font-mono font-bold tracking-[0.5em] bg-background border-2 border-border rounded-lg focus:border-profit focus:outline-none text-foreground placeholder:text-muted-foreground/50 placeholder:text-base placeholder:tracking-normal"
            data-testid="passcode-input"
          />

          {/* Submit button */}
          <button
            type="button"
            disabled={passcode.length < 4 || isLoggingIn}
            onClick={() => doLogin(passcode)}
            className={`w-full h-14 text-lg font-bold rounded-lg transition-all ${
              passcode.length >= 4 && !isLoggingIn
                ? "bg-profit text-black active:scale-[0.98]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
            data-testid="pin-submit"
          >
            {isLoggingIn ? "Unlocking..." : "Unlock"}
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
